import { c as _c } from "react/compiler-runtime";
import { Badge, Heading, LayoutStack } from 'design-system/client';
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import { memo, useMemo } from 'react';
import styles from './SupportedProjects.module.css';
import type { ProjectProps, ProjectStateBadgeProps, SupportedProjectsProps } from './types';
import TextLink from '@/components/TextLink';
import { RailsRoutes } from '@/helpers/utils';
function ProjectStateBadge(t0) {
  const $ = _c(7);
  const {
    closed,
    completed,
    id
  } = t0;
  const t = useTranslations("nextjs.receiver_pages.fundraising_event.project");
  let t1;
  if ($[0] !== closed || $[1] !== completed || $[2] !== t) {
    t1 = closed ? t("ended") : completed ? t("financed") : undefined;
    $[0] = closed;
    $[1] = completed;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const label = t1;
  if (!label) {
    return null;
  }
  let t2;
  if ($[4] !== id || $[5] !== label) {
    t2 = <Badge as="span" className={styles.badge} id={id}>{label}</Badge>;
    $[4] = id;
    $[5] = label;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function Project(t0) {
  const $ = _c(19);
  const {
    id,
    aasmState,
    title,
    locale,
    completedAt,
    carrier
  } = t0;
  const hasBadge = aasmState === "closed" || !!completedAt;
  const fallbackLocale = locale === "de" ? "en" : "de";
  const t1 = aasmState === "closed";
  const t2 = !!completedAt;
  const t3 = `badge-${id}`;
  let t4;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3) {
    t4 = <ProjectStateBadge closed={t1} completed={t2} id={t3} />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== id || $[5] !== locale) {
    t5 = RailsRoutes.projectPage({
      locale,
      slug: id
    });
    $[4] = id;
    $[5] = locale;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = hasBadge ? `badge-${id}` : undefined;
  const t7 = title?.[locale] ?? title?.[fallbackLocale];
  let t8;
  if ($[7] !== t5 || $[8] !== t6 || $[9] !== t7) {
    t8 = <TextLink prefetch={false} href={t5} aria-describedby={t6}>{t7}</TextLink>;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t4 || $[12] !== t8) {
    t9 = <span>{t4}{t8}</span>;
    $[11] = t4;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== carrier) {
    t10 = carrier && <div className={styles.imageContainer}><Image alt="" src={carrier.profilePicture} width={100} height={100} /></div>;
    $[14] = carrier;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t9) {
    t11 = <div className={styles.project}>{t9}{t10}</div>;
    $[16] = t10;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  return t11;
}
function SupportedProjects(t0) {
  const $ = _c(19);
  const {
    projects,
    publicFaceName
  } = t0;
  const t = useTranslations("nextjs.receiver_pages.fundraising_event.project");
  const locale = useLocale();
  const numberOfProjects = projects?.nodes?.length;
  let t1;
  if ($[0] !== numberOfProjects || $[1] !== t) {
    t1 = numberOfProjects && numberOfProjects > 1 ? t("headline_multiple_projects", {
      number: numberOfProjects
    }) : t("headline_single_project");
    $[0] = numberOfProjects;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <Heading as="h3" level="heading-xs" className={styles.title}>{t1}</Heading>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== locale || $[6] !== projects) {
    t3 = projects && (projects?.nodes || [])?.map(project => {
      if (!project) {
        return null;
      }
      return <li key={project?.id}><Project {...project} locale={locale} /></li>;
    });
    $[5] = locale;
    $[6] = projects;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <ul>{t3}</ul>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== publicFaceName || $[11] !== t) {
    t5 = t("disclaimer", {
      name: publicFaceName
    });
    $[10] = publicFaceName;
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = <p className={styles.disclaimer}>{t5}</p>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t2 || $[16] !== t4 || $[17] !== t6) {
    t7 = <LayoutStack className={styles.container} space="300">{t2}{t4}{t6}</LayoutStack>;
    $[15] = t2;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
export default memo(SupportedProjects);